import { computed, makeObservable, observable } from 'mobx';
import last from 'lodash/last';

import type { FileViewModel } from 'chaika-frontend-types/frontend-cms';

import { parseFileName } from '@utils/parseFileName';
import { EntryFileCategory } from 'chaika-enums';

import { DateHelper } from '@utils/DateHelper';
import { Validation } from '@utils/Validation';
import { assertIsDefined } from '@utils/assertIsDefined';

export class FileAsset<D = object> {
    @observable public path: string;
    @observable public authorId: string;
    @observable public createdAt: Date;
    @observable public category?: EntryFileCategory;
    @observable public details?: D;

    constructor({ path, authorId, createdAt, category, details }: FileViewModel) {
        makeObservable(this);

        this.path = path;
        this.authorId = authorId;
        this.category = category;
        this.createdAt = new Date(createdAt);
        this.details = details as D;
    }

    @computed
    public get name(): string {
        if (Validation.isUrl(this.path)) {
            return `${DateHelper.toLocalStringDate(this.createdAt, true)}.url`;
        }

        const fileName = last(this.path.split('/'));
        assertIsDefined(fileName, 'Splitting a file path always yields at least one segment');
        return fileName;
    }

    @computed
    public get title(): string {
        let title = this.name;

        const parts = title.split('.');
        if (parts.length > 1) {
            title = parts.length === 2 ? parts[0] : parts.slice(0, -1).join('.');
        }

        return parseFileName(title);
    }

    @computed
    public get extension(): string {
        const parts = this.name.split('.');
        if (parts.length <= 1) {
            return '';
        }

        const extension = last(parts);
        assertIsDefined(extension, 'File name with a dot has a last part');
        return extension;
    }
}
